import React from 'react';
import propTypes from 'prop-types';
import moment from 'moment-timezone';
import { FormGroup, Label, Input, Row, Col } from 'reactstrap';
import ChecklistBox, { singleOf, idsOf } from '../../../components/Export/ChecklistBox';
import { courierOptions, paymentMethods } from '../../../constants/kickavenue';

const sectionStyle = { fontSize: '.66rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase' };
const labelStyle = { fontWeight: 600, fontSize: '.78rem' };

// The twelve sale pipeline views Go /admins/sales accepts in `type`.
export const STATUS_OPTIONS = [
  { id: 'outstanding', name: 'Outstanding' },
  { id: 'outstanding_confirmation', name: 'Outstanding confirmation' },
  { id: 'outstanding_delivery', name: 'Outstanding delivery' },
  { id: 'quality_control', name: 'Quality control' },
  { id: 'verification_process', name: 'Legit check' },
  { id: 'verification_passed', name: 'Verification passed' },
  { id: 'pending_processed', name: 'Pending processed' },
  { id: 'processed', name: 'Processed' },
  { id: 'follow_up', name: 'Follow up' },
  { id: 'follow_up_approval', name: 'Follow up approval' },
  { id: 'need_refund', name: 'Need refund' },
  { id: 'failed', name: 'Failed' }
];
export const COURIER_OPTIONS = courierOptions.map(c => ({ id: c.value, name: c.name }));
export const PAYMENT_OPTIONS = paymentMethods.map(p => ({ id: p.value, name: p.label }));
export const FULFILMENT_OPTIONS = [{ id: '0', name: 'Ready stock' }, { id: '1', name: 'Pre-order' }];

const daysAgo = n => moment().tz('Asia/Jakarta').subtract(n, 'days').format('YYYY-MM-DD');

export const SALES_EXPORT_DEFAULTS = {
  status: [],
  keyword: '',
  category: [],
  courier: [],
  payment_method: [],
  seller: [],
  ka_verified_by: '',
  fulfilment: [],
  date_field: 'created_at',
  date_from: daysAgo(30),
  date_to: daysAgo(0)
};

const Field = ({ id, label, children }) => (
  <FormGroup>
    <Label for={id} className="mb-1" style={labelStyle}>{label}</Label>
    {children}
  </FormGroup>
);

// SalesExportFilters is the Sales block inside ExportModal, shared by All
// Sales and Processed Sales. categoryOptions come from the page's category
// list; loadSellers reads GET /admins/users.
export default function SalesExportFilters({ filters, setFilter, disabled, categoryOptions, loadSellers }) {
  const box = (name, props) => (
    <ChecklistBox id={`_exportSales_${name}`} value={filters[name]} onChange={v => setFilter(name, v)} disabled={disabled} {...props} />
  );
  const categories = (categoryOptions || []).map(c => ({ id: c.label || c.name, name: c.label || c.name }));
  return (
    <div>
      <div className="text-muted mb-2" style={sectionStyle}>Order</div>
      <Row>
        <Col md={6}><Field id="_exportSales_status" label="Status">{box('status', { options: STATUS_OPTIONS, placeholder: 'All statuses' })}</Field></Col>
        <Col md={6}><Field id="_exportSales_keyword" label="Keyword">
          <Input id="_exportSales_keyword" bsSize="sm" value={filters.keyword} disabled={disabled} placeholder="Invoice, buyer email, product" onChange={e => setFilter('keyword', e.target.value)} />
        </Field></Col>
      </Row>
      <Row>
        <Col md={4}><Field id="_exportSales_category" label="Category">{box('category', { options: categories, placeholder: 'All categories' })}</Field></Col>
        <Col md={4}><Field id="_exportSales_courier" label="Courier">{box('courier', { options: COURIER_OPTIONS, placeholder: 'All couriers' })}</Field></Col>
        <Col md={4}><Field id="_exportSales_payment_method" label="Payment method">{box('payment_method', { options: PAYMENT_OPTIONS, searchable: true, placeholder: 'All' })}</Field></Col>
      </Row>
      <Row>
        <Col md={4}><Field id="_exportSales_seller" label="Seller">{box('seller', { loadOptions: loadSellers, searchable: true, placeholder: 'All sellers' })}</Field></Col>
        <Col md={4}><Field id="_exportSales_ka_verified_by" label="Verified by">
          <Input id="_exportSales_ka_verified_by" bsSize="sm" value={filters.ka_verified_by} disabled={disabled} placeholder="Admin email" onChange={e => setFilter('ka_verified_by', e.target.value)} />
        </Field></Col>
        <Col md={4}><Field id="_exportSales_fulfilment" label="Fulfilment">{box('fulfilment', { options: FULFILMENT_OPTIONS })}</Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Dates</div>
      <Row>
        <Col md={4}><Field id="_exportSales_date_field" label="Date field">
          <Input id="_exportSales_date_field" bsSize="sm" type="select" value={filters.date_field} disabled={disabled} onChange={e => setFilter('date_field', e.target.value)}>
            <option value="created_at">Order date</option>
            <option value="customer_paid">Customer paid</option>
            <option value="ka_verified_date">Verified date</option>
            <option value="ka_sent">Sent to buyer</option>
          </Input>
        </Field></Col>
        <Col md={4}><Field id="_exportSales_date_from" label="From">
          <Input id="_exportSales_date_from" bsSize="sm" type="date" value={filters.date_from} disabled={disabled} onChange={e => setFilter('date_from', e.target.value)} />
        </Field></Col>
        <Col md={4}><Field id="_exportSales_date_to" label="To">
          <Input id="_exportSales_date_to" bsSize="sm" type="date" value={filters.date_to} disabled={disabled} onChange={e => setFilter('date_to', e.target.value)} />
        </Field></Col>
      </Row>
    </div>
  );
}

SalesExportFilters.propTypes = {
  filters: propTypes.object.isRequired,
  setFilter: propTypes.func.isRequired,
  disabled: propTypes.bool,
  categoryOptions: propTypes.array,
  loadSellers: propTypes.func.isRequired
};

// normalizeSalesFilters turns the page's raw `seller`/`voucher` UI objects into
// the seller_id/voucher_id/voucher_code the server expects, the same conversion
// _getList does, so the inherited page filters match the export.
export const normalizeSalesFilters = (params = {}) => {
  let p = { ...params };
  if (p.voucher) {
    const { voucher, ...other } = p;
    p = Number(voucher.id) ? { ...other, voucher_id: voucher.id } : { ...other, voucher_code: voucher.code };
  }
  if (p.seller && p.seller.id) {
    const { seller, ...other } = p;
    p = { ...other, seller_id: seller.id };
  }
  return p;
};

// toSalesParams maps the modal's values onto Go /admins/sales query
// parameters. The page's own params come first; an empty box inherits the
// page's value, a pick overrides it. Single-value filters on the server
// (category, courier, payment method, seller) take a pick only when exactly
// one option is ticked, the same rule the other screens use.
const onlyOne = value => ((value || []).length === 1 ? String(value[0].id) : '');

export const toSalesParams = (panelParams, f) => {
  const { page, per_page, sort_by, ...base } = panelParams;
  const params = { ...base };
  const override = (key, value) => {
    if (value !== '' && value !== null && value !== undefined) params[key] = value;
  };
  override('type', idsOf(f.status));
  override('keyword', (f.keyword || '').trim());
  override('category', onlyOne(f.category));
  override('courier', singleOf(f.courier, COURIER_OPTIONS));
  override('payment_method', singleOf(f.payment_method, PAYMENT_OPTIONS));
  override('seller_id', onlyOne(f.seller));
  override('ka_verified_by', (f.ka_verified_by || '').trim());
  override('pre_order', singleOf(f.fulfilment, FULFILMENT_OPTIONS));
  if (f.date_from || f.date_to) {
    override('date_field', f.date_field);
    override('date_from', f.date_from);
    override('date_to', f.date_to);
  }
  return params;
};
